import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, filter, map, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { TwoColumnFormData } from 'src/app/components/shared/form/two-column-form/two-column-form.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { LocationGroup, LocationProfile } from 'src/app/core/models/admin';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { GROUP_LABEL, GroupItem, TABS } from '../location-profile.constants';

/* --------
 * This component is responsible for managing LOCATION GROUP
 * assignments within the Location Profile workflow.
 *
 * It supports both:
 *  - Create mode  (navigation to next tab only)
 *  - Edit mode    (immediate API update + persistence)
 *
 * The component integrates with a two-column selection UI
 * where:
 *  - Left column  → Available groups
 *  - Right column → Assigned groups
 *
 * KEY RESPONSIBILITIES:
 * ---------------------
 * ✔ Load all available location groups from backend
 * ✔ Display assigned groups for the current location
 * ✔ Track group assignment changes
 * ✔ Emit form validity / dirty state to parent component
 * ✔ Persist group changes via API
 * ✔ Reset group assignments safely
 */
@Component({
  selector: 'app-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss'],
  standalone: false,
})
export class GroupComponent implements OnInit {
  @Input() locationItem!: LocationProfile;
  @Input() edit!: boolean;

  changeTab = output<unknown>();
  formStatus = output<boolean>();

  readonly CONSTANTS = { ...GROUP_LABEL };
  readonly IMAGES = IMAGE_SRC;

  allGroups!: TwoColumnFormData[];
  assignedGroupData: TwoColumnFormData[] = [];
  copyOfInitialState: TwoColumnFormData[] = [];

  allGroupData!: GroupItem[];
  displayedId!: string[];
  isGroupChanged = false;

  destroyRef = inject(DestroyRef);

  constructor(
    private readonly locationService: LocationService,
    private readonly errorHandler: ErrorhandleService,
    private readonly commonUtils: CommonUtils,
    private readonly matDialog: MatDialog,
  ) {}

  /**
   * ngOnInit()
   *
   * Called once when the component is initialized.
   *
   * Responsibilities:
   * -----------------
   * 1. Load all available groups
   * 2. Map currently assigned groups
   * 3. Capture initial state for reset functionality
   */
  ngOnInit(): void {
    this.initGroupProps();
    this.copyOfInitialState = this.mapLocationGroups();
  }

  /**
   * Handles form submission based on mode.
   *
   * CREATE MODE:
   * ------------
   * - Emits tab change event to move to Configuration tab
   *
   * EDIT MODE:
   * ----------
   * - Persists group changes immediately via API
   * - Displays success banner
   * - Resets internal state on success
   */
  onSubmit(): void {
    if (!this.edit) {
      this.changeTab.emit({
        data: { profile: this.locationItem, edit: this.edit },
        tab: TABS.CONFIGURATION,
      });
    } else {
      // ✅ Normalize GMT-style timezone before sending (collapse offsets to "GMT")
      this.locationItem.gmtTimeZone = this.normalizeGmtTz(
        this.locationItem.gmtTimeZone,
      );

      this.locationService
        .updateLocation(this.locationItem, this.locationItem?.locationCode)
        .pipe(
          take(1),
          map((res) => {
            this.matDialog.closeAll();
            if (res?.code === 200 && res?.response) {
              this.resetFormState(res?.response);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
          }),
          catchError((res) => {
            this.errorHandler.handleError<GroupComponent>(
              res,
              res.code,
              'Location Profile: Failed to update profile in group form component.',
            );
            return of(false);
          }),
          filter((val) => !!val),
        )
        .subscribe();
    }
  }

  /**
   * Resets internal state after a successful API update.
   *
   * - Updates locationItem with latest backend response
   * - Resets initial state snapshot
   * - Updates global profile state
   * - Clears dirty flags
   */
  resetFormState(profile: LocationProfile): void {
    // ✅ Normalize inbound profile so offsets don't creep back into state/UI
    profile.gmtTimeZone = this.normalizeGmtTz(profile.gmtTimeZone);

    // update locationItem & state
    if (profile) {
      this.locationItem = profile;
      this.copyOfInitialState = this.mapLocationGroups();
      this.locationService.setProfileState$(this.locationItem);
      this.isGroupChanged = false;
      this.formStatus.emit(false);
      this.initGroupProps();
    }
  }

  /**
   * Resets group assignments back to original state.
   *
   * - Restores assigned groups from initial snapshot
   * - Persists reset to backend
   * - Clears dirty state
   */
  resetGroups(): void {
    this.assignedGroupData = this.copyOfInitialState;
    this.isGroupChanged = false;
    this.formStatus.emit(this.isGroupChanged);
    this.locationItem = this.getAssignedGroups();

    // ✅ Normalize before sending on reset flow as well
    this.locationItem.gmtTimeZone = this.normalizeGmtTz(
      this.locationItem.gmtTimeZone,
    );

    this.locationService
      .updateLocation(this.locationItem, this.locationItem?.locationCode)
      .pipe(
        take(1),
        map((res) => {
          if (res?.code === 200 && res?.response) {
            this.resetFormState(res?.response);
          }
        }),
        catchError((res) => {
          this.errorHandler.handleError<GroupComponent>(
            res,
            res.code,
            'Location Profile: Failed to reset profile in group form component.',
          );
          return of(false);
        }),
      )
      .subscribe();
  }

  /**
   * Receives group change state from child component.
   * Used to enable/disable save actions.
   */
  handleUpdateSubmit($event: boolean) {
    this.isGroupChanged = $event;
    this.formStatus.emit($event);
  }

  /**
   * Updates assigned groups based on user selection.
   * Filters selected groups and updates locationItem.
   */
  handleUpdateAssignedValues($event: TwoColumnFormData[]) {
    const value = $event
      .filter((value) => !!value.selected)
      .map((value) => value.data as LocationGroup);
    this.locationItem.locationGroups = value;
  }

  /**
   * Sorts groups alphabetically by group code.
   * Used for consistent display order.
   */
  private sortGroups(groups: LocationGroup[]) {
    return groups?.slice().sort((a, b) => a.code?.localeCompare(b.code));
  }

  /**
   * Initializes group-related properties.
   *
   * - Fetches all available groups
   * - Maps assigned groups from location profile
   */
  private initGroupProps() {
    this.locationService
      .getLocationGroups()
      .pipe(take(1))
      .subscribe((data) => {
        const all = this.sortGroups(data?.response?.locationGroups) || [];
        // Exclude groups that are already assigned to avoid duplicates
        const assignedCodes = new Set(
          (this.locationItem?.locationGroups || []).map((g) => g.code),
        );
        this.allGroups = all
          .filter((g) => !assignedCodes.has(g.code))
          .map((value) => {
            return { selected: false, data: value };
          });
      });

    this.assignedGroupData = (
      this.locationItem?.locationGroups
        ? this.sortGroups(this.locationItem.locationGroups)
        : []
    ).map((value) => {
      return { selected: false, data: value };
    });
  }

  /**
   * Maps assigned group data back into the LocationProfile model.
   * Used before sending data to backend.
   */
  private getAssignedGroups(): LocationProfile {
    this.locationItem.locationGroups = this.assignedGroupData.map(
      (value) => value.data as LocationGroup,
    );
    return this.locationItem;
  }

  /**
   * Creates a snapshot of assigned groups.
   * Used to support reset functionality.
   */
  private mapLocationGroups() {
    return (
      this.locationItem?.locationGroups?.map((value) => {
        return { data: value, selected: false };
      }) || []
    );
  }

  /**
   * Normalize "GMT" style values (e.g., "GMT-05:00", "GMT+01") to "GMT".
   * Keep IANA zones like "America/New_York", stripping any descriptions like " (Eastern Time)".
   */
  private normalizeGmtTz(input?: string): string | undefined {
    if (!input) return input;
    const trimmed = input.trim();
    if (/^GMT/i.test(trimmed)) {
      return 'GMT';
    }
    return trimmed.split(/\s|\(/)[0];
  }
}
